import { Link } from "react-router-dom";
import { student_routes } from "../../../router/all_routes";
import { useAcademicYear } from "../../../../core/common/context/AcademicYearContext";
import { useEffect, useState } from "react";
import { getStudentAbsenceByYear } from "../../../../api/etudiants";
import { toast } from "sonner";
import { getSessionsByYear } from "../../../../api/settingEDT";

type Absence = {
  month: string;
  day: string;
  start: string;
  end: string;
  subject: string;
  teacher: string;
  teacherAvatar: string;
  justified: boolean;
  reason?: string;
};

const StudentAttendance = () => {
  const routes = student_routes;

  const { selectedYear } = useAcademicYear();

  const [absencesData, setAbsencesData] = useState<any[]>([]);
  const [sessionData, setSessionData] = useState<any[]>([]);

  const fecthSessionData = async () => {
    try {
      const response = await getSessionsByYear(selectedYear?.id);
      // console.log("Sessions", response);
      setSessionData(response.data || []);
    } catch (error: any) {
      // console.log("erreur de chargement des sessions : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!selectedYear?.id) return;
    fecthSessionData();
  }, [selectedYear?.id]);

  const fecthAbsencesData = async () => {
    try {
      const response = await getStudentAbsenceByYear(
        selectedYear?.Student?.Class?.id,
        selectedYear?.Student?.id,
      );
      // console.log("Absences", response);
      setAbsencesData(response.data || []);
    } catch (error: any) {
      // console.log("erreur de chargement des Absences : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!selectedYear?.id || !selectedYear?.Student?.Class?.id) return;
    fecthAbsencesData();
  }, [selectedYear?.id, selectedYear?.Student?.Class?.id]);

  const getSessionTimeRange = (sessionIds: string[]) => {
    if (!sessionIds?.length || !sessionData.length) {
      return { start: "--:--", end: "--:--" };
    }

    // récupérer les sessions correspondantes
    const sessions = sessionData
      .filter((s: any) => sessionIds.includes(s.id))
      .sort((a: any, b: any) => a.start_time.localeCompare(b.start_time));

    if (!sessions.length) {
      return { start: "--:--", end: "--:--" };
    }

    return {
      start: sessions[0].start_time.slice(0, 5),
      end: sessions[sessions.length - 1].end_time.slice(0, 5),
    };
  };

  // Grouper par mois puis par jour
  const grouped = absencesData.reduce((acc: any, item: any) => {
    const date = new Date(item.CourseVersionEDT.date_course);

    const month = date.toLocaleDateString("fr-FR", {
      month: "long",
      year: "numeric",
    });

    const day = date.toLocaleDateString("fr-FR", {
      day: "2-digit",
    });

    const { start, end } = getSessionTimeRange(item.id_session);

    const absence: Absence = {
      month,
      day,
      start,
      end,
      subject: item.CourseVersionEDT.CourseClass.Course.Subject.label,
      teacher: `${item.CourseVersionEDT.CourseClass.Course.TeacherProfil.firstname}
              ${item.CourseVersionEDT.CourseClass.Course.TeacherProfil.lastname}`,
      teacherAvatar: "", // optionnel
      justified: item.justified,
      reason: item.id_justificatif ? "Justifiée" : undefined,
    };

    if (!acc[month]) acc[month] = {};
    if (!acc[month][day]) acc[month][day] = [];

    acc[month][day].push(absence);

    return acc;
  }, {});

  // // 🔹 Compteurs bruts
  const total = absencesData.length;
  const justified = absencesData.filter((a) => a.justified).length;
  const notJustified = total - justified;

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Mes Absences</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.studentDashboard}>Accueil</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Absences
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}

          <div className="card">
            <div className="card-header">
              <h4 className="mb-3">Résumé des Absences</h4>
              <div className="d-flex justify-content-around text-center">
                <div>
                  <h5 className="mb-0">{total}</h5>
                  <small className="text-muted">Total</small>
                </div>
                <div>
                  <h5 className="mb-0 text-success">{justified}</h5>
                  <small className="text-success">Justifiées</small>
                </div>
                <div>
                  <h5 className="mb-0 text-danger">{notJustified}</h5>
                  <small className="text-danger">Non Justifiées</small>
                </div>
              </div>
            </div>
            <div className="card-body">
              {Object.entries(
                grouped as Record<string, Record<string, Absence[]>>,
              ).map(([month, days]) => (
                <div key={month} className="mb-5">
                  <h5 className="bg-light p-2 rounded mb-3">{month}</h5>
                  <div className="d-flex flex-column gap-3">
                    {Object.entries(days as Record<string, Absence[]>).map(
                      ([day, sessions]) =>
                        sessions.map((s, idx) => (
                          <div
                            key={idx}
                            className="p-3 border rounded shadow-sm"
                            style={{
                              backgroundColor: s.justified
                                ? "#e9f7ef"
                                : "#f8d7da",
                            }}
                          >
                            {/* HEADER */}
                            <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                              <h6 className="text-truncate mb-0">
                                {s.subject}
                              </h6>

                              <span
                                className={`badge flex-shrink-0 ${
                                  s.justified ? "bg-success" : "bg-danger"
                                }`}
                              >
                                {s.justified && s.reason
                                  ? s.reason
                                  : "Non Justifié"}
                              </span>
                            </div>

                            {/* BODY */}
                            <div className="d-flex align-items-center">
                              <span
                                className={`avatar avatar-md me-3 rounded-circle flex-shrink-0 ${
                                  s.justified
                                    ? "bg-success-transparent"
                                    : "bg-danger-transparent"
                                }`}
                              >
                                <i className="ti ti-book fs-16" />
                              </span>

                              <div className="overflow-hidden">
                                <p className="mb-0">
                                  <i className="ti ti-calendar me-2" />
                                  Le {day} {month} [{s.start} - {s.end}]
                                </p>
                                <p className="mb-0">
                                  <i className="ti ti-user me-2" />
                                  {s.teacher}
                                </p>
                              </div>
                            </div>
                          </div>
                        )),
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentAttendance;
